import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { useAlert } from 'context/AlertContext';
import {
  confirmPayment,
  clearCheckoutManifest,
  setCheckoutManifest,
  isUsableCheckoutManifest,
  isStartableCheckoutManifest,
  getMultibancoMethod,
  isMultibancoActive,
} from '../../redux/actions/payments';
import { getBooking } from '../../redux/actions/bookings';

const CHECKOUT_SCRIPT_VERSION =
  process.env.REACT_APP_EASYPAY_CHECKOUT_SDK_VERSION || '2.9.1';
const CHECKOUT_SCRIPT_SRC =
  process.env.REACT_APP_EASYPAY_CHECKOUT_SDK_URL ||
  `https://cdn.easypay.pt/checkout/${CHECKOUT_SCRIPT_VERSION}/`;
const SESSION_MANIFEST_PREFIX = 'easypayCheckoutManifest:';
const SESSION_MANIFEST_ENV_PREFIX = 'easypayCheckoutEnv:';
// Pre-scoping keys — cleared on mount so old stubs can't render blank widgets.
const LEGACY_SESSION_MANIFEST_KEY = 'easypayCheckoutManifest';
const LEGACY_SESSION_MANIFEST_ENV_KEY = 'easypayCheckoutEnv';
const RECENT_PAYMENT_KEY = 'recentPaymentBookingId';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const normalizeStatus = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const summarizeCheckoutPayload = (data) => ({
  checkoutId: data?.id || null,
  methodType:
    data?.payment?.method ||
    data?.method ||
    data?.transaction?.method ||
    data?.paymentMethod ||
    null,
  paymentStatus:
    data?.payment?.status ||
    data?.transaction?.status ||
    data?.payment_status ||
    data?.status ||
    null,
  paymentId: data?.payment?.id || data?.transaction?.id || null,
  checkoutStatus: data?.checkout?.status || data?.status || null,
  value:
    data?.payment?.value || data?.transaction?.value || data?.value || null,
});

const checkoutLog = (event, details = {}) => {
  if (process.env.REACT_APP_EASYPAY_CHECKOUT_DEBUG !== 'true') return;

  const payload = {
    event,
    timestamp: new Date().toISOString(),
    ...details,
  };

  try {
    const key = details.bookingId
      ? `easypayCheckoutDebugLog:${details.bookingId}`
      : 'easypayCheckoutDebugLog';
    const current = JSON.parse(sessionStorage.getItem(key) || '[]');
    current.push(payload);
    const clipped = current.slice(-100);
    sessionStorage.setItem(key, JSON.stringify(clipped));
    window.__T2B_EASYPAY_CHECKOUT_LOGS__ = clipped;
  } catch {}
};

const getConfirmBooking = (res) =>
  res?.booking || res?.data?.booking || res?.result?.booking || null;

const isBookingPaid = (booking) =>
  normalizeStatus(booking?.status) === 'paid' ||
  normalizeStatus(booking?.paymentStatus) === 'paid';

const isConfirmPaid = (res, refreshedBooking = null) =>
  res?.paid === true ||
  res?.data?.paid === true ||
  isBookingPaid(getConfirmBooking(res)) ||
  isBookingPaid(refreshedBooking);

const getConfirmStatus = (res) =>
  normalizeStatus(res?.status || res?.data?.status || res?.result?.status);

const isConfirmPending = (res) => {
  if (isConfirmPaid(res)) return false;
  const status = getConfirmStatus(res);
  if (
    ['failed', 'error', 'cancelled', 'canceled', 'expired'].includes(status)
  ) {
    return false;
  }
  return (
    res?.paid === false ||
    res?.data?.paid === false ||
    status === 'pending' ||
    status === 'processing' ||
    status === 'waiting' ||
    res?.pendingWebhook === true ||
    res?.data?.pendingWebhook === true
  );
};

const getPendingPaymentDetails = (res = {}) => ({
  paymentMethod:
    res.paymentMethod ||
    res.data?.paymentMethod ||
    res.checkoutInfo?.payment?.method,
  mbEntity:
    res.mbEntity || res.data?.mbEntity || res.checkoutInfo?.payment?.entity,
  mbReference:
    res.mbReference ||
    res.data?.mbReference ||
    res.checkoutInfo?.payment?.reference,
  mbAmount:
    res.mbAmount ||
    res.data?.mbAmount ||
    res.checkoutInfo?.payment?.value ||
    res.checkoutInfo?.payment?.amount,
  mbExpiresAt:
    res.mbExpiresAt ||
    res.data?.mbExpiresAt ||
    res.checkoutInfo?.payment?.expiration_date ||
    res.checkoutInfo?.payment?.expires_at,
});

const enrichManifestWithCheckoutInfo = (currentManifest, checkoutInfo) => {
  if (!checkoutInfo || typeof checkoutInfo !== 'object') {
    return currentManifest;
  }

  const payment = checkoutInfo.payment || {};
  const method =
    payment.method === 'mb'
      ? {
          type: 'mb',
          ...(payment.entity ? { entity: payment.entity } : {}),
          ...(payment.reference ? { reference: payment.reference } : {}),
          ...(payment.value != null ? { value: payment.value } : {}),
          ...(payment.amount != null ? { amount: payment.amount } : {}),
          ...(payment.expiration_date
            ? { expiration_date: payment.expiration_date }
            : {}),
          ...(payment.expires_at ? { expires_at: payment.expires_at } : {}),
        }
      : payment.method;

  return {
    ...(currentManifest || {}),
    ...checkoutInfo,
    id: checkoutInfo.id || currentManifest?.id,
    checkoutInfo,
    payment: {
      ...(currentManifest?.payment || {}),
      ...payment,
      ...(method ? { method } : {}),
    },
  };
};

const buildPendingMbManifest = (mb) => ({
  id: mb?.checkoutId,
  checkoutId: mb?.checkoutId,
  _pendingPayment: true,
  paymentMethod: 'mb',
  mbEntity: mb?.entity,
  mbReference: mb?.reference,
  mbAmount: mb?.value,
  mbExpiresAt: mb?.expiration_date,
  payment: {
    method: {
      type: 'mb',
      entity: mb?.entity,
      reference: mb?.reference,
      value: mb?.value,
      expiration_date: mb?.expiration_date,
    },
  },
});

const logConfirmResult = ({
  bookingId,
  checkoutId,
  confirmResponse,
  finalUiState,
}) => {
  const payload = {
    bookingId,
    checkoutId,
    confirmPaid: confirmResponse?.paid ?? confirmResponse?.data?.paid,
    confirmStatus:
      confirmResponse?.status ??
      confirmResponse?.data?.status ??
      confirmResponse?.result?.status,
    providerStatus:
      confirmResponse?.providerStatus ?? confirmResponse?.data?.providerStatus,
    paymentMethod:
      confirmResponse?.paymentMethod ?? confirmResponse?.data?.paymentMethod,
    finalUiStateRendered: finalUiState,
  };
  checkoutLog('confirm_result', payload);
};

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams();

  const reduxManifest = useSelector((state) => state.payments.checkoutManifest);

  // IMPORTANT:
  // Do NOT rely on process.env.NODE_ENV for staging/prod detection in the browser.
  // In React builds it's usually "production" for both staging + production.
  // Use hostname instead (runtime-safe).
  const isProdHost =
    window.location.hostname === 'there2b.com' ||
    window.location.hostname === 'www.there2b.com';

  const checkoutTesting = !isProdHost; // sandbox on staging/local, prod on prod domain

  const bookingId = searchParams.get('bookingId');
  const eventId = searchParams.get('eventId');
  // EasyPay appends the checkout id to the return URL (typically `?id=...`).
  // We persist it as the authoritative checkoutId for /confirm so the backend
  // can reconcile via CheckoutSession history even if the booking's current
  // paymentCheckoutId pointer has since moved on.
  const urlCheckoutId =
    searchParams.get('id') ||
    searchParams.get('checkoutId') ||
    searchParams.get('checkout_id') ||
    null;

  const sessionManifestKey = bookingId
    ? `${SESSION_MANIFEST_PREFIX}${bookingId}`
    : null;
  const sessionManifestEnvKey = bookingId
    ? `${SESSION_MANIFEST_ENV_PREFIX}${bookingId}`
    : null;

  const checkoutInstanceRef = useRef(null);
  const containerRef = useRef(null);
  const finalizedRef = useRef(false);
  const stageRef = useRef('loading');

  const [stage, setStage] = useState('loading'); // loading | rendering | multibanco | processing | success | pending | closed | error | missing
  const [scriptReady, setScriptReady] = useState(!!window.easypayCheckout);
  const [manifest, setManifest] = useState(reduxManifest || null);
  const [recoveredStatus, setRecoveredStatus] = useState(null); // null | 'paid' | 'approved' | 'expired' | 'other'
  const [copiedField, setCopiedField] = useState(null);
  const [pendingPaymentDetails, setPendingPaymentDetails] = useState(null);
  const [checkoutBooting, setCheckoutBooting] = useState(false);

  useEffect(() => {
    stageRef.current = stage;
  }, [stage]);

  const traceCheckout = useCallback(
    (label, details = {}) => {
      const payload = {
        ...details,
        bookingId,
        stage: stageRef.current,
        finalized: finalizedRef.current,
        hostname: window.location.hostname,
        checkoutTesting,
        sdkScripts: Array.from(document.scripts)
          .map((s) => s.src)
          .filter(Boolean)
          .filter((src) => src.includes('easypay')),
      };
      checkoutLog(label, payload);
    },
    [bookingId, checkoutTesting]
  );

  const unmountCheckoutWidget = useCallback(
    (reason = 'unspecified') => {
      if (checkoutInstanceRef.current) {
        traceCheckout('[Checkout] cleanupCheckout/unmount', { reason });
        try {
          checkoutInstanceRef.current.unmount();
        } catch {}
        checkoutInstanceRef.current = null;
      }
    },
    [traceCheckout]
  );

  const cleanupCheckout = useCallback(
    (reason = 'unspecified', options = {}) => {
      traceCheckout('[Checkout] cleanupCheckout/unmount', { reason });
      unmountCheckoutWidget(reason);
      dispatch(clearCheckoutManifest());

      try {
        if (!options.preserveSessionManifest) {
          if (sessionManifestKey) sessionStorage.removeItem(sessionManifestKey);
          if (sessionManifestEnvKey)
            sessionStorage.removeItem(sessionManifestEnvKey);
        }
        sessionStorage.removeItem(LEGACY_SESSION_MANIFEST_KEY);
        sessionStorage.removeItem(LEGACY_SESSION_MANIFEST_ENV_KEY);
      } catch {}
    },
    [
      dispatch,
      sessionManifestKey,
      sessionManifestEnvKey,
      traceCheckout,
      unmountCheckoutWidget,
    ]
  );

  const goBackToBooking = useCallback(() => {
    traceCheckout('[Checkout] navigation away from checkout page', {
      reason: 'goBackToBooking',
    });
    cleanupCheckout('goBackToBooking', {
      preserveSessionManifest: stageRef.current === 'multibanco',
    });
    if (eventId) {
      const qs = new URLSearchParams({
        tab: 'my-events',
        openEventManager: '1',
        eventId,
        emTab: 'booking-status',
      }).toString();
      navigate(`/dashboard?${qs}`);
    } else {
      navigate('/dashboard?tab=my-events');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanupCheckout, navigate, eventId]);

  const safeShowAlert = useCallback(
    (opts) => {
      showAlert(opts);
    },
    [showAlert]
  );

  const callConfirmPayment = useCallback(
    (reason, args) => {
      traceCheckout('[Checkout] confirmPayment called', {
        reason,
        checkoutId: args?.checkoutId,
        checkoutInfo: summarizeCheckoutPayload(args?.checkoutInfo),
      });
      return dispatch(confirmPayment(args));
    },
    [dispatch, traceCheckout]
  );

  const setTerminalError = useCallback(
    (reason, details = {}, alertOptions = null) => {
      traceCheckout('[Checkout] set terminal error', { reason, ...details });
      finalizedRef.current = true;
      setStage('error');
      if (alertOptions) safeShowAlert(alertOptions);
    },
    [safeShowAlert, traceCheckout]
  );

  // Keep a single source of truth for manifest:
  // - prefer redux
  // - fallback to sessionStorage (if user refreshes)
  // Also: protect against env mismatch (prod vs sandbox) causing 404s in the widget.
  useEffect(() => {
    const currentEnv = isProdHost ? 'prod' : 'sandbox';

    // Always drop legacy unscoped keys — they may contain pre-fix stubs.
    try {
      sessionStorage.removeItem(LEGACY_SESSION_MANIFEST_KEY);
      sessionStorage.removeItem(LEGACY_SESSION_MANIFEST_ENV_KEY);
    } catch {}

    if (reduxManifest) {
      if (!isUsableCheckoutManifest(reduxManifest)) {
        dispatch(clearCheckoutManifest());
        setManifest(null);
        setStage('missing');
        return;
      }
      setManifest(reduxManifest);
      if (sessionManifestKey && sessionManifestEnvKey) {
        try {
          sessionStorage.setItem(
            sessionManifestKey,
            JSON.stringify(reduxManifest)
          );
          sessionStorage.setItem(sessionManifestEnvKey, currentEnv);
        } catch {}
      }
      return;
    }

    if (!manifest && sessionManifestKey && sessionManifestEnvKey) {
      try {
        const stored = sessionStorage.getItem(sessionManifestKey);
        const storedEnv = sessionStorage.getItem(sessionManifestEnvKey);

        if (stored) {
          // If env changed since manifest was stored, discard it.
          if (storedEnv && storedEnv !== currentEnv) {
            sessionStorage.removeItem(sessionManifestKey);
            sessionStorage.removeItem(sessionManifestEnvKey);
            setManifest(null);
            setStage('missing');
            return;
          }

          const parsed = JSON.parse(stored);
          if (!isUsableCheckoutManifest(parsed)) {
            sessionStorage.removeItem(sessionManifestKey);
            sessionStorage.removeItem(sessionManifestEnvKey);
            setManifest(null);
            setStage('missing');
            return;
          }
          setManifest(parsed);
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxManifest, isProdHost, sessionManifestKey, sessionManifestEnvKey]);

  // Recovery: when manifest is missing (session expired / browser closed),
  // check the booking's current status from the API so we can tell the user
  // what actually happened instead of a generic "session expired" message.
  useEffect(() => {
    if (stage !== 'missing' || !bookingId) return;

    let cancelled = false;
    (async () => {
      try {
        const booking = await dispatch(getBooking(bookingId));
        if (cancelled) return;

        const bookingRecord = booking?.data || booking;
        const s = normalizeStatus(bookingRecord?.status);
        if (isBookingPaid(bookingRecord)) {
          setRecoveredStatus('paid');
          // Also store the recent-payment signal for BookingStatus
          try {
            sessionStorage.setItem(
              RECENT_PAYMENT_KEY,
              JSON.stringify({ bookingId, eventId, timestamp: Date.now() })
            );
          } catch {}
        } else if (s === 'approved') {
          setRecoveredStatus('approved');
        } else if (s === 'expired' || s === 'rejected') {
          setRecoveredStatus(s);
        } else {
          setRecoveredStatus('other');
        }
      } catch {
        if (!cancelled) setRecoveredStatus('other');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stage, bookingId, eventId, dispatch]);

  // If the user opens "View reference" from an older/cached manifest that only
  // contains the amount, refetch the booking and merge the active MB details
  // returned by the API.
  useEffect(() => {
    if (stage !== 'multibanco' || !bookingId) return;
    const currentMb = getMultibancoMethod(manifest);
    if (!currentMb?.pendingOnly) return;

    let cancelled = false;
    (async () => {
      try {
        const booking = await dispatch(getBooking(bookingId));
        if (cancelled) return;

        const bookingRecord = booking?.data || booking;
        const apiMb = getMultibancoMethod(bookingRecord?.activePayment);
        if (!apiMb || apiMb.pendingOnly) return;

        const enrichedManifest = {
          ...(manifest || {}),
          ...buildPendingMbManifest(apiMb),
        };
        setManifest(enrichedManifest);
        dispatch(setCheckoutManifest(enrichedManifest));
        setPendingPaymentDetails({
          paymentMethod: 'mb',
          mbEntity: apiMb.entity,
          mbReference: apiMb.reference,
          mbAmount: apiMb.value,
          mbExpiresAt: apiMb.expiration_date,
        });

        try {
          if (sessionManifestKey && sessionManifestEnvKey) {
            sessionStorage.setItem(
              sessionManifestKey,
              JSON.stringify(enrichedManifest)
            );
            sessionStorage.setItem(
              sessionManifestEnvKey,
              isProdHost ? 'prod' : 'sandbox'
            );
          }
        } catch {}
      } catch {
        // Keep the existing pending panel; the slow poll below may still enrich it.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    stage,
    bookingId,
    manifest,
    dispatch,
    sessionManifestKey,
    sessionManifestEnvKey,
    isProdHost,
  ]);

  // Slow-poll /confirm while Multibanco reference is active.
  // Multibanco payments settle at ATMs/homebanking — can take hours — so we
  // check occasionally (not aggressively) while this tab is open.
  useEffect(() => {
    if (stage !== 'multibanco' || !bookingId) return;
    if (!isMultibancoActive(manifest)) return;

    const MB_POLL_MS = 30_000;
    const MB_POLL_TIMEOUT_MS = 2 * 60_000;
    let cancelled = false;
    let interval = null;
    let timeout = null;
    let lastResponse = null;

    const tick = async () => {
      try {
        const checkoutId = urlCheckoutId || manifest?.id;
        const res = await callConfirmPayment('multibanco_slow_poll', {
          bookingId,
          checkoutId,
          checkoutInfo: manifest?.checkoutInfo,
        });
        lastResponse = res;
        if (cancelled) return;

        const paid = isConfirmPaid(res);

        if (paid) {
          logConfirmResult({
            bookingId,
            checkoutId,
            confirmResponse: res,
            finalUiState: 'paid',
          });
          try {
            sessionStorage.setItem(
              RECENT_PAYMENT_KEY,
              JSON.stringify({ bookingId, eventId, timestamp: Date.now() })
            );
          } catch {}
          setStage('success');
          safeShowAlert({
            type: 'success',
            title: t('alert_dialog.checkout.payment_success_title'),
            message: t('alert_dialog.checkout.payment_success_message'),
            primaryAction: {
              label: t('alert_dialog.checkout.payment_success_primary_action'),
            },
          });
          if (interval) clearInterval(interval);
          if (timeout) clearTimeout(timeout);
          cleanupCheckout('multibanco_slow_poll_paid');
        } else if (isConfirmPending(res)) {
          const details = getPendingPaymentDetails(res);
          if (details.mbEntity || details.mbReference) {
            setPendingPaymentDetails(details);
            const enrichedManifest = {
              ...(manifest || {}),
              ...buildPendingMbManifest({
                checkoutId,
                entity: details.mbEntity,
                reference: details.mbReference,
                value: details.mbAmount,
                expiration_date: details.mbExpiresAt,
              }),
            };
            setManifest(enrichedManifest);
            dispatch(setCheckoutManifest(enrichedManifest));
            try {
              if (sessionManifestKey && sessionManifestEnvKey) {
                sessionStorage.setItem(
                  sessionManifestKey,
                  JSON.stringify(enrichedManifest)
                );
                sessionStorage.setItem(
                  sessionManifestEnvKey,
                  isProdHost ? 'prod' : 'sandbox'
                );
              }
            } catch {}
          }
        } else if (!isConfirmPending(res)) {
          logConfirmResult({
            bookingId,
            checkoutId,
            confirmResponse: res,
            finalUiState: 'failed',
          });
          if (interval) clearInterval(interval);
          if (timeout) clearTimeout(timeout);
        }
      } catch {
        // swallow — transient network errors shouldn't stop polling
      }
    };

    interval = setInterval(tick, MB_POLL_MS);
    timeout = setTimeout(() => {
      if (cancelled) return;
      const checkoutId = urlCheckoutId || manifest?.id;
      logConfirmResult({
        bookingId,
        checkoutId,
        confirmResponse: lastResponse,
        finalUiState: 'pending',
      });
      if (interval) clearInterval(interval);
    }, MB_POLL_TIMEOUT_MS);

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [
    stage,
    manifest,
    bookingId,
    eventId,
    urlCheckoutId,
    callConfirmPayment,
    dispatch,
    safeShowAlert,
    cleanupCheckout,
    sessionManifestKey,
    sessionManifestEnvKey,
    isProdHost,
    t,
  ]);

  const copyToClipboard = useCallback(async (value, field) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(field);
      setTimeout(() => setCopiedField((c) => (c === field ? null : c)), 1500);
    } catch {}
  }, []);

  useEffect(() => {
    if (stage !== 'rendering') {
      setCheckoutBooting(false);
      return undefined;
    }

    const el = containerRef.current;
    if (!el) return undefined;

    const hasWidgetContent = () =>
      el.childElementCount > 0 || el.textContent.trim().length > 0;

    setCheckoutBooting(!hasWidgetContent());
    if (hasWidgetContent()) return undefined;

    let revealTimer = null;
    const observer = new MutationObserver(() => {
      if (!hasWidgetContent()) return;
      revealTimer = setTimeout(() => setCheckoutBooting(false), 200);
      observer.disconnect();
    });

    observer.observe(el, { childList: true, subtree: true });

    const fallbackTimer = setTimeout(() => setCheckoutBooting(false), 5000);

    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
      if (revealTimer) clearTimeout(revealTimer);
    };
  }, [stage]);

  // Load checkout widget script (if needed)
  useEffect(() => {
    if (!bookingId) {
      setTerminalError(
        'missing_booking_id',
        {},
        {
          type: 'error',
          title: t('alert_dialog.checkout.payment_failed_title'),
          message: t('alert_dialog.checkout.payment_failed_message'),
          primaryAction: {
            label: t('alert_dialog.checkout.payment_failed_primary_action'),
          },
        }
      );
      return;
    }

    if (!manifest) {
      if (finalizedRef.current) return;
      setStage('missing');
      return;
    }

    if (window.easypayCheckout) {
      checkoutLog('sdk_script_already_present', {
        bookingId,
        scriptUrl: CHECKOUT_SCRIPT_SRC,
        scriptVersion: CHECKOUT_SCRIPT_VERSION,
        hostname: window.location.hostname,
        checkoutTesting,
      });
      setScriptReady(true);
      return;
    }

    const existing = document.querySelector(
      `script[src="${CHECKOUT_SCRIPT_SRC}"]`
    );
    if (existing) {
      const onLoad = () => setScriptReady(true);
      existing.addEventListener('load', onLoad);
      return () => existing.removeEventListener('load', onLoad);
    }

    const script = document.createElement('script');
    script.src = CHECKOUT_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      checkoutLog('sdk_script_loaded', {
        bookingId,
        scriptUrl: CHECKOUT_SCRIPT_SRC,
        scriptVersion: CHECKOUT_SCRIPT_VERSION,
        hostname: window.location.hostname,
        checkoutTesting,
      });
      setScriptReady(true);
    };
    script.onerror = () => {
      setTerminalError(
        'sdk_script_load_error',
        { scriptUrl: CHECKOUT_SCRIPT_SRC },
        {
          type: 'error',
          title: t('alert_dialog.checkout.payment_widget_load_title'),
          message: t('alert_dialog.checkout.payment_widget_load_message'),
          primaryAction: {
            label: t(
              'alert_dialog.checkout.payment_widget_load_primary_action'
            ),
          },
        }
      );
    };
    document.body.appendChild(script);
  }, [bookingId, checkoutTesting, manifest, setTerminalError, t]);

  // Confirm helper (server-side verification) with light polling to wait for webhook.
  //
  // /confirm is READ-ONLY — it does not finalize payment. It may return:
  //   { paid: true, status: "paid" }         → booking is paid
  //   { paid: false, status: "pending" }     → checkout/reference exists, payment not confirmed
  //   { paid: false, status: "failed" }      → no payable confirmation detected
  //
  // Returns: { finalUiState: 'paid' | 'pending' | 'failed', confirmResponse }
  const pollConfirmPayment = useCallback(
    async ({ tries = 8, delayMs = 1500, checkoutId, checkoutInfo } = {}) => {
      if (!bookingId) {
        return { finalUiState: 'failed', confirmResponse: null };
      }

      let lastResponse = null;
      let sawPending = false;

      for (let i = 0; i < tries; i++) {
        try {
          checkoutLog('confirm_request', {
            bookingId,
            checkoutId,
            attempt: i + 1,
            attempts: tries,
            checkoutInfo: summarizeCheckoutPayload(checkoutInfo),
          });
          const res = await callConfirmPayment('pollConfirmPayment', {
            bookingId,
            checkoutId,
            checkoutInfo,
          });
          lastResponse = res;

          let refreshedBooking = null;
          try {
            refreshedBooking = await dispatch(getBooking(bookingId));
          } catch {
            // A failed refresh should not hide a paid confirm response.
          }

          if (isConfirmPaid(res, refreshedBooking)) {
            logConfirmResult({
              bookingId,
              checkoutId,
              confirmResponse: res,
              finalUiState: 'paid',
            });
            return {
              finalUiState: 'paid',
              confirmResponse: res,
              booking: refreshedBooking || getConfirmBooking(res),
            };
          }

          if (isConfirmPending(res)) {
            sawPending = true;
          } else {
            logConfirmResult({
              bookingId,
              checkoutId,
              confirmResponse: res,
              finalUiState: 'failed',
            });
            return {
              finalUiState: 'failed',
              confirmResponse: res,
              booking: refreshedBooking || getConfirmBooking(res),
            };
          }
        } catch {
          // ignore transient errors while polling
        }

        if (i < tries - 1) await sleep(delayMs);
      }

      const finalUiState = sawPending ? 'pending' : 'failed';
      logConfirmResult({
        bookingId,
        checkoutId,
        confirmResponse: lastResponse,
        finalUiState,
      });
      return { finalUiState, confirmResponse: lastResponse };
    },
    [callConfirmPayment, dispatch, bookingId]
  );

  // Start the checkout widget
  useEffect(() => {
    if (!manifest || !scriptReady) return;
    if (!containerRef.current) return;
    if (checkoutInstanceRef.current) return;

    const el = document.getElementById('easypay-checkout');
    if (!el) return;

    // Final guard: never hand the SDK a stub/invalid manifest — it would render
    // an empty container with no error. Surface it as "missing" instead.
    if (!isUsableCheckoutManifest(manifest)) {
      cleanupCheckout('invalid_manifest_before_start_checkout');
      setManifest(null);
      setStage('missing');
      return;
    }

    // Multibanco is handled in a dedicated panel — no SDK widget needed.
    if (getMultibancoMethod(manifest)) {
      setStage('multibanco');
      return;
    }

    if (!isStartableCheckoutManifest(manifest)) {
      checkoutLog('invalid_manifest_before_start_checkout', {
        bookingId,
        manifestId: manifest?.id,
        manifestHasSession:
          typeof manifest?.session === 'string' && manifest.session.length > 0,
        manifestKeys:
          manifest && typeof manifest === 'object'
            ? Object.keys(manifest).sort()
            : [],
      });
      cleanupCheckout('invalid_manifest_before_start_checkout');
      setManifest(null);
      setStage('missing');
      return;
    }

    setCheckoutBooting(true);
    setStage('rendering');

    const raf = requestAnimationFrame(() => {
      try {
        checkoutLog('start_checkout_call', {
          bookingId,
          scriptUrl: CHECKOUT_SCRIPT_SRC,
          scriptVersion: CHECKOUT_SCRIPT_VERSION,
          hostname: window.location.hostname,
          checkoutTesting,
          manifestId: manifest?.id,
          manifestHasSession:
            typeof manifest?.session === 'string' && manifest.session.length > 0,
          manifestKeys:
            manifest && typeof manifest === 'object'
              ? Object.keys(manifest).sort()
              : [],
          sdkGlobalPresent: !!window.easypayCheckout,
          options: {
            id: 'easypay-checkout',
            testing: checkoutTesting,
            display: 'inline',
          },
        });

        checkoutInstanceRef.current = window.easypayCheckout.startCheckout(
          manifest,
          {
            id: 'easypay-checkout',
            testing: checkoutTesting,

            // IMPORTANT:
            // - Do NOT mark paid from the frontend
            // - Only call our backend confirm endpoint (which verifies with Easypay / checks DB updated by webhook)
            onSuccess: async (data) => {
              const checkoutId = data?.id || urlCheckoutId || manifest?.id;

              checkoutLog('on_success', {
                bookingId,
                hostname: window.location.hostname,
                checkoutTesting,
                manifestId: manifest?.id,
                checkoutId,
                summary: summarizeCheckoutPayload(data),
              });

              if (finalizedRef.current) return;

              const enrichedManifest = enrichManifestWithCheckoutInfo(
                manifest,
                data
              );
              const mbMethod = getMultibancoMethod(enrichedManifest);
              const checkoutInfoForConfirm = mbMethod ? data : undefined;

              if (mbMethod && !mbMethod.pendingOnly) {
                unmountCheckoutWidget('multibanco_reference_display');
                setManifest(enrichedManifest);
                dispatch(setCheckoutManifest(enrichedManifest));
                setPendingPaymentDetails({
                  checkoutInfo: data,
                  paymentMethod: 'mb',
                  mbEntity: mbMethod.entity,
                  mbReference: mbMethod.reference,
                  mbAmount: mbMethod.value,
                  mbExpiresAt: mbMethod.expiration_date,
                });
                setStage('multibanco');

                try {
                  if (sessionManifestKey && sessionManifestEnvKey) {
                    sessionStorage.setItem(
                      sessionManifestKey,
                      JSON.stringify(enrichedManifest)
                    );
                    sessionStorage.setItem(
                      sessionManifestEnvKey,
                      isProdHost ? 'prod' : 'sandbox'
                    );
                  }
                } catch {}

                try {
                  const res = await callConfirmPayment(
                    'multibanco_on_success_reference_confirm',
                    {
                      bookingId,
                      checkoutId,
                      checkoutInfo: checkoutInfoForConfirm,
                    }
                  );
                  const confirmedMbDetails = getPendingPaymentDetails({
                    ...(res || {}),
                    checkoutInfo: data,
                  });
                  setPendingPaymentDetails((prev) => ({
                    ...(prev || {}),
                    ...confirmedMbDetails,
                  }));

                  if (
                    confirmedMbDetails.mbEntity ||
                    confirmedMbDetails.mbReference ||
                    confirmedMbDetails.mbExpiresAt
                  ) {
                    const confirmedManifest = {
                      ...enrichedManifest,
                      ...buildPendingMbManifest({
                        checkoutId,
                        entity:
                          confirmedMbDetails.mbEntity || mbMethod.entity,
                        reference:
                          confirmedMbDetails.mbReference ||
                          mbMethod.reference,
                        value: confirmedMbDetails.mbAmount ?? mbMethod.value,
                        expiration_date:
                          confirmedMbDetails.mbExpiresAt ||
                          mbMethod.expiration_date,
                      }),
                    };
                    setManifest(confirmedManifest);
                    dispatch(setCheckoutManifest(confirmedManifest));
                    try {
                      if (sessionManifestKey && sessionManifestEnvKey) {
                        sessionStorage.setItem(
                          sessionManifestKey,
                          JSON.stringify(confirmedManifest)
                        );
                        sessionStorage.setItem(
                          sessionManifestEnvKey,
                          isProdHost ? 'prod' : 'sandbox'
                        );
                      }
                    } catch {}
                  }

                  if (isConfirmPaid(res)) {
                    finalizedRef.current = true;
                    try {
                      sessionStorage.setItem(
                        RECENT_PAYMENT_KEY,
                        JSON.stringify({
                          bookingId,
                          eventId,
                          timestamp: Date.now(),
                        })
                      );
                    } catch {}
                    setStage('success');
                    safeShowAlert({
                      type: 'success',
                      title: t('alert_dialog.checkout.payment_success_title'),
                      message: t(
                        'alert_dialog.checkout.payment_success_message'
                      ),
                      primaryAction: {
                        label: t(
                          'alert_dialog.checkout.payment_success_primary_action'
                        ),
                      },
                    });
                    cleanupCheckout('multibanco_confirm_paid_after_reference');
                  }
                } catch {
                  // The reference is already visible; a transient confirm failure
                  // should not hide it from the user.
                }
                return;
              }

              finalizedRef.current = true;
              setStage('processing');

              try {
                const pollResult = await pollConfirmPayment({
                  checkoutId,
                  checkoutInfo: checkoutInfoForConfirm,
                });

                if (pollResult?.finalUiState === 'paid') {
                  try {
                    sessionStorage.setItem(
                      RECENT_PAYMENT_KEY,
                      JSON.stringify({
                        bookingId,
                        eventId,
                        timestamp: Date.now(),
                      })
                    );
                  } catch {}

                  setStage('success');
                  safeShowAlert({
                    type: 'success',
                    title: t('alert_dialog.checkout.payment_success_title'),
                    message: t('alert_dialog.checkout.payment_success_message'),
                    primaryAction: {
                      label: t(
                        'alert_dialog.checkout.payment_success_primary_action'
                      ),
                    },
                  });
                } else if (pollResult?.finalUiState === 'pending') {
                  setPendingPaymentDetails(
                    getPendingPaymentDetails({
                      ...(pollResult.confirmResponse || {}),
                      checkoutInfo: data,
                    })
                  );
                  setStage('pending');
                  safeShowAlert({
                    type: 'info',
                    title: t('alert_dialog.checkout.payment_pending_title'),
                    message: t('alert_dialog.checkout.payment_pending_message'),
                    primaryAction: {
                      label: t('checkout.back_to_dashboard'),
                    },
                  });
                } else {
                  setTerminalError(
                    'non_card_confirm_failed',
                    {
                      checkoutId,
                      providerStatus:
                        pollResult?.confirmResponse?.providerStatus ||
                        pollResult?.confirmResponse?.data?.providerStatus,
                    },
                    {
                      type: 'error',
                      title: t('alert_dialog.checkout.payment_failed_title'),
                      message: t(
                        'alert_dialog.checkout.payment_failed_message'
                      ),
                      primaryAction: {
                        label: t(
                          'alert_dialog.checkout.payment_failed_primary_action'
                        ),
                      },
                    }
                  );
                }
              } catch (err) {
                checkoutLog('confirm_poll_error', {
                  bookingId,
                  checkoutId,
                  message: err?.message,
                });
                setTerminalError(
                  'non_card_confirm_poll_exception',
                  {
                    checkoutId,
                    message: err?.message,
                  },
                  {
                    type: 'error',
                    title: t('alert_dialog.checkout.payment_widget_load_title'),
                    message: t('alert_dialog.checkout.payment_widget_load_message'),
                    primaryAction: {
                      label: t(
                        'alert_dialog.checkout.payment_widget_load_primary_action'
                      ),
                    },
                  }
                );
              } finally {
                cleanupCheckout('checkout_after_on_success');
              }
            },

            onClose: (...payload) => {
              checkoutLog('on_close', {
                bookingId,
                hostname: window.location.hostname,
                checkoutTesting,
                manifestId: manifest?.id,
                summary: payload.map(summarizeCheckoutPayload),
              });

              setStage((prev) =>
                prev === 'success' || prev === 'error'
                  ? prev
                  : prev === 'pending' || prev === 'processing'
                    ? 'pending'
                    : 'closed'
              );
              finalizedRef.current = true;
              cleanupCheckout('checkout_closed_without_active_card_flow');
            },

            onError: (error) => {
              checkoutLog('on_error', {
                bookingId,
                hostname: window.location.hostname,
                checkoutTesting,
                manifestId: manifest?.id,
                payload: {
                  code: error?.code,
                  message: error?.message,
                },
              });
              setTerminalError(
                'sdk_on_error',
                {
                  code: error?.code,
                  message: error?.message,
                },
                {
                  type: 'error',
                  title: t('alert_dialog.checkout.payment_failed_title'),
                  message: t('alert_dialog.checkout.payment_failed_message'),
                  primaryAction: {
                    label: t(
                      'alert_dialog.checkout.payment_failed_primary_action'
                    ),
                  },
                }
              );
              cleanupCheckout('sdk_on_error');
            },
          }
        );
      } catch (e) {
        console.error('startCheckout error:', e);
        setTerminalError(
          'start_checkout_exception',
          { message: e?.message },
          {
            type: 'error',
            title: t('alert_dialog.checkout.payment_widget_load_title'),
            message: t('alert_dialog.checkout.payment_widget_load_message'),
            primaryAction: {
              label: t(
                'alert_dialog.checkout.payment_widget_load_primary_action'
              ),
            },
          }
        );
      }
    });

    return () => {
      cancelAnimationFrame(raf);
      unmountCheckoutWidget('checkout_effect_cleanup');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    manifest,
    scriptReady,
    bookingId,
    eventId,
    dispatch,
    pollConfirmPayment,
    cleanupCheckout,
    setTerminalError,
    unmountCheckoutWidget,
    safeShowAlert,
    t,
    checkoutTesting,
    urlCheckoutId,
    sessionManifestKey,
    sessionManifestEnvKey,
    isProdHost,
  ]);

  return (
    <div className="p-10 min-h-screen bg-gray-50 flex flex-col items-center">
      <style>
        {`
          #easypay-checkout.easypay-widget-frame {
            display: flex;
            justify-content: center;
            align-items: flex-start;
          }

          #easypay-checkout.easypay-widget-frame > *,
          #easypay-checkout.easypay-widget-frame iframe {
            margin-left: auto !important;
            margin-right: auto !important;
          }
        `}
      </style>
      <h1 className="text-3xl font-bold text-center mb-8">
        {t('checkout.title')}
      </h1>

      <div className="w-full max-w-2xl min-h-[260px] mx-auto bg-white shadow rounded-xl p-6 relative flex items-center justify-center">
        {(stage === 'loading' ||
          stage === 'processing' ||
          (stage === 'rendering' && checkoutBooting)) && (
          <div className="absolute inset-0 flex flex-col gap-3 items-center justify-center bg-white/90 z-10 rounded-xl px-6 text-center">
            <ClipLoader size={40} />
            {stage === 'processing' && (
              <p className="text-sm text-gray-600 max-w-sm">
                {t('checkout.processing') ||
                  'Confirming your payment… this can take a few seconds.'}
              </p>
            )}
            {stage === 'rendering' && checkoutBooting && (
              <p className="text-sm text-gray-600 max-w-sm">
                {t('checkout.loading_widget')}
              </p>
            )}
          </div>
        )}

        <div
          id="easypay-checkout"
          ref={containerRef}
          className={`${
            stage === 'rendering' ? 'easypay-widget-frame' : 'hidden'
          } max-w-[720px] w-full min-h-[600px] mx-auto`}
        />

        {stage === 'multibanco' &&
          (() => {
            const mb = getMultibancoMethod(manifest);
            if (!mb) return null;
            const fmtAmount = (v) => {
              const n = Number(v);
              return Number.isFinite(n)
                ? new Intl.NumberFormat(i18n?.language || 'pt-PT', {
                    style: 'currency',
                    currency: 'EUR',
                  }).format(n)
                : `${v} €`;
            };
            const fmtExpiry = (iso) => {
              if (!iso) return null;
              const d = new Date(iso);
              return Number.isNaN(d.getTime())
                ? iso
                : d.toLocaleString(i18n?.language || 'pt-PT');
            };
            const Row = ({ label, value, field }) => (
              <div className="flex items-center justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
                <span className="text-sm text-gray-500">{label}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm sm:text-base font-semibold text-gray-900 tracking-wider">
                    {value}
                  </span>
                  {field && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(value, field)}
                      className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-gray-600"
                    >
                      {copiedField === field
                        ? t('checkout.copied') || 'Copied'
                        : t('checkout.copy') || 'Copy'}
                    </button>
                  )}
                </div>
              </div>
            );
            const hasReferenceDetails =
              mb.entity ||
              mb.reference ||
              mb.value != null ||
              mb.expiration_date;
            return (
              <div className="w-full max-w-md py-6 sm:py-8 flex flex-col items-center text-center">
                <h2 className="text-xl sm:text-2xl font-semibold mb-2">
                  {t('checkout.multibanco_title') || 'Pay at any ATM'}
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  {t('checkout.multibanco_body') ||
                    'Use the entity and reference below at any Multibanco ATM or homebanking. Your booking will be confirmed automatically once payment is received.'}
                </p>

                {hasReferenceDetails && (
                  <div className="w-full bg-gray-50 border border-gray-200 rounded-xl px-5 py-3 mb-4 text-left">
                    {mb.entity && (
                      <Row
                        label={t('checkout.multibanco_entity') || 'Entity'}
                        value={mb.entity}
                        field="entity"
                      />
                    )}
                    {mb.reference && (
                      <Row
                        label={
                          t('checkout.multibanco_reference') || 'Reference'
                        }
                        value={mb.reference}
                        field="reference"
                      />
                    )}
                    {mb.value != null && (
                      <Row
                        label={t('checkout.multibanco_amount') || 'Amount'}
                        value={fmtAmount(mb.value)}
                      />
                    )}
                    {mb.expiration_date && (
                      <Row
                        label={t('checkout.multibanco_expires') || 'Pay before'}
                        value={fmtExpiry(mb.expiration_date)}
                      />
                    )}
                  </div>
                )}

                <p className="text-xs text-gray-500 mb-6">
                  {t('checkout.multibanco_poll_hint') ||
                    'We’re checking for your payment. You can safely leave this page — the booking will update automatically.'}
                </p>

                <button
                  onClick={goBackToBooking}
                  className="px-5 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-900"
                >
                  {t('checkout.back_to_dashboard') || 'Back to dashboard'}
                </button>
              </div>
            );
          })()}

        {stage === 'success' && (
          <div className="flex flex-col items-center text-center py-16">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="text-2xl font-semibold mb-2">
              {t('checkout.success_title') || 'Payment received'}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('checkout.success_body', { bookingId }) ||
                `We’ll reflect your payment for booking #${bookingId} in your dashboard shortly.`}
            </p>
            <button
              onClick={goBackToBooking}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              {t('checkout.back_to_dashboard') || 'Back to dashboard'}
            </button>
          </div>
        )}

        {stage === 'pending' && (
          <div className="flex flex-col items-center text-center py-16 max-w-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mb-4 text-amber-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z"
              />
            </svg>
            <h2 className="text-2xl font-semibold mb-2">
              {t('checkout.pending_title') || 'Payment pending'}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('checkout.pending_body') ||
                'The payment has not been confirmed yet. If you chose Multibanco, use the entity/reference shown to complete the payment. As soon as we receive confirmation from EasyPay, the booking will update automatically.'}
            </p>
            {(pendingPaymentDetails?.mbEntity ||
              pendingPaymentDetails?.mbReference ||
              pendingPaymentDetails?.mbAmount) && (
              <div className="w-full bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 mb-6 text-left text-sm">
                {pendingPaymentDetails?.mbEntity && (
                  <div className="flex justify-between gap-4 py-1">
                    <span className="text-amber-800/70">
                      {t('checkout.multibanco_entity') || 'Entity'}
                    </span>
                    <span className="font-mono font-semibold">
                      {pendingPaymentDetails.mbEntity}
                    </span>
                  </div>
                )}
                {pendingPaymentDetails?.mbReference && (
                  <div className="flex justify-between gap-4 py-1">
                    <span className="text-amber-800/70">
                      {t('checkout.multibanco_reference') || 'Reference'}
                    </span>
                    <span className="font-mono font-semibold">
                      {pendingPaymentDetails.mbReference}
                    </span>
                  </div>
                )}
                {pendingPaymentDetails?.mbAmount && (
                  <div className="flex justify-between gap-4 py-1">
                    <span className="text-amber-800/70">
                      {t('checkout.multibanco_amount') || 'Amount'}
                    </span>
                    <span className="font-mono font-semibold">
                      {pendingPaymentDetails.mbAmount}
                    </span>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={goBackToBooking}
              className="px-5 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-900"
            >
              {t('checkout.back_to_dashboard') || 'Back to dashboard'}
            </button>
          </div>
        )}

        {stage === 'closed' && (
          <div className="flex flex-col items-center text-center py-16">
            <h2 className="text-xl font-semibold mb-2">
              {t('checkout.closed_title') || 'Checkout closed'}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('checkout.closed_body') ||
                'You can reopen the payment from your booking.'}
            </p>
            <button
              onClick={goBackToBooking}
              className="px-5 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-900"
            >
              {t('checkout.go_back') || 'Go back'}
            </button>
          </div>
        )}

        {stage === 'error' && (
          <div className="flex flex-col items-center text-center py-16">
            <h2 className="text-xl font-semibold mb-2">
              {t('checkout.error_title') || 'Something went wrong'}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('checkout.error_body') ||
                'Please try again from your booking page.'}
            </p>
            <button
              onClick={goBackToBooking}
              className="px-5 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
            >
              {t('checkout.back') || 'Back to dashboard'}
            </button>
          </div>
        )}

        {stage === 'missing' && (
          <div className="flex flex-col items-center text-center py-16">
            {/* Check if booking was actually paid (e.g. browser closed after payment) */}
            {recoveredStatus === 'paid' ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 mb-4 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h2 className="text-2xl font-semibold mb-2">
                  {t('checkout.already_paid_title') ||
                    'Payment already confirmed'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {t('checkout.already_paid_body') ||
                    'Your payment for this booking has been confirmed. You can view it in your dashboard.'}
                </p>
              </>
            ) : recoveredStatus === 'approved' ? (
              <>
                <h2 className="text-xl font-semibold mb-2">
                  {t('checkout.expired_title') || 'Payment session expired'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {t('checkout.expired_still_payable') ||
                    'Your checkout session expired, but the booking is still awaiting payment. You can start a new checkout from your dashboard.'}
                </p>
              </>
            ) : recoveredStatus === 'expired' ||
              recoveredStatus === 'rejected' ? (
              <>
                <h2 className="text-xl font-semibold mb-2">
                  {t('checkout.booking_no_longer_payable_title') ||
                    'Booking no longer payable'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {t('checkout.booking_no_longer_payable_body') ||
                    'This booking has expired or been cancelled. Please check your dashboard for details.'}
                </p>
              </>
            ) : recoveredStatus === null ? (
              <>
                <ClipLoader size={30} />
                <p className="text-sm text-gray-500 mt-3">
                  {t('checkout.checking_status') || 'Checking booking status…'}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold mb-2">
                  {t('checkout.expired_title') || 'Payment session expired'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {t('checkout.expired_body') ||
                    'Please start the checkout again from your booking.'}
                </p>
              </>
            )}
            {recoveredStatus !== null && (
              <button
                onClick={goBackToBooking}
                className="px-5 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-900"
              >
                {t('checkout.back_to_dashboard') || 'Back to dashboard'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;
